import React, { useState, useEffect, useRef, useCallback } from 'react';
import PatentCollisionMap3D from '../../components/chemistry/PatentCollisionMap3D';

const CHANNEL_NAME = 'patent-map';

interface MapData {
  molecules: any[];
  patents: any[];
  summary: {
    total_molecules: number;
    enriched_molecules: number;
    zone_counts: Record<string, number>;
    total_patents: number;
  };
}

const PatentMapPopoutPage: React.FC = () => {
  const [mapData, setMapData] = useState<MapData | null>(null);
  const [selectedMolecule, setSelectedMolecule] = useState<any>(null);
  const [visibleZones, setVisibleZones] = useState<number[]>([1, 2, 3, 4, 5]);
  const channelRef = useRef<BroadcastChannel | null>(null);

  // Open channel and listen for init/update messages from parent
  useEffect(() => {
    const ch = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = ch;

    ch.onmessage = (e) => {
      if (e.data?.type === 'init') {
        setMapData(e.data.mapData);
        if (e.data.selectedMolecule) setSelectedMolecule(e.data.selectedMolecule);
        if (e.data.visibleZones) setVisibleZones(e.data.visibleZones);
      }
      if (e.data?.type === 'select-molecule') {
        setSelectedMolecule(e.data.molecule);
      }
      if (e.data?.type === 'update-zones') {
        setVisibleZones(e.data.visibleZones);
      }
    };

    // Tell parent we're ready
    ch.postMessage({ type: 'popout-ready' });

    return () => ch.close();
  }, []);

  // Notify parent when this window closes
  useEffect(() => {
    const handleUnload = () => {
      channelRef.current?.postMessage({ type: 'popout-closed' });
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  // When molecule is selected in popout, broadcast to parent
  const handleMoleculeSelect = useCallback((mol: any) => {
    setSelectedMolecule(mol);
    channelRef.current?.postMessage({ type: 'select-molecule', molecule: mol });
  }, []);

  if (!mapData) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0a0a0f', fontFamily: 'system-ui, sans-serif' }}>
        <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: '14px', fontWeight: 500 }}>Waiting for patent map data...</div>
          <div style={{ fontSize: '12px', marginTop: '8px', color: 'var(--text-muted)' }}>Open from the 3D Globe view in the editor</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100vw', height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <PatentCollisionMap3D
        mapData={mapData}
        onMoleculeSelect={handleMoleculeSelect}
        selectedMolecule={selectedMolecule}
        visibleZones={visibleZones}
      />
    </div>
  );
};

export default PatentMapPopoutPage;
